import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast } from '@socialchorus/shared-ui-components';
import { usePatronSelector } from '../../common/use-patron-selector';
import programSelectors from '../../models/program/selectors';
import { useChatbotStreaming } from '../../common/streaming/use-chatbot-streaming';
import { Suggestion } from '../../models/chatbot/types';
import {
  trackChatbotMessageSend,
  trackChatbotMessageSuccess,
  trackChatbotMessageError,
  trackChatbotMessageTimeout,
  trackChatbotSuggestionClick,
} from '../../models/assistant/analytics/analytics';

import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import './chatbot.scss';

interface ChatbotProps {
  threadId?: string;
  isThreadPersisted?: boolean;
  onThreadChange?: (threadId: string, persisted: boolean) => void;
  className?: string;
  suggestions?: Suggestion[];
  shouldFocusInput?: boolean;
  onFocusHandled?: () => void;
}

const Chatbot: React.FC<ChatbotProps> = ({
  threadId,
  isThreadPersisted = false,
  onThreadChange,
  className = '',
  suggestions = [],
  shouldFocusInput,
  onFocusHandled,
}) => {
  const [messageInput, setMessageInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showTimeoutError, setShowTimeoutError] = useState(false);
  const pendingMessageRef = useRef<string | null>(null);
  const currentThreadIdRef = useRef(threadId);

  const { t } = useTranslation();
  const programId = usePatronSelector(programSelectors.getProgramId);
  const streamMessage = useChatbotStreaming(programId);

  // Reset sending state when switching threads so the user can submit immediately
  useEffect(() => {
    currentThreadIdRef.current = threadId;
    setIsSending(false);
    setShowTimeoutError(false);
  }, [threadId]);

  const handleSendMessage = async (messageToSend?: string) => {
    const message = messageToSend || messageInput.trim();

    if (!message || isSending || !programId) return;

    // Reset timeout error state for fresh attempt
    setShowTimeoutError(false);

    trackChatbotMessageSend({
      thread_id: threadId,
      is_new_conversation: !threadId,
      message_length: message.length,
    });

    // Store message for potential restoration on timeout
    pendingMessageRef.current = message;
    setMessageInput('');

    try {
      // Track which thread this stream creates (for new conversations)
      let streamCreatedThreadId: string | undefined;
      // Start streaming - IDs will come via stream_start event
      streamMessage(
        { threadId, message: message },
        (event) => {
          switch (event.type) {
            case 'stream_start':
              // If this is a new conversation, update the threadId immediately but mark as not persisted
              // Only navigate if user hasn't switched away since sending
              if (!threadId && event.data?.threadId && onThreadChange) {
                if (currentThreadIdRef.current === threadId) {
                  streamCreatedThreadId = event.data.threadId;
                  onThreadChange(event.data.threadId, false);
                }
              }
              // Set typing indicator after user message is added to cache
              setIsSending(true);
              break;

            case 'stream_end': {
              // Clear pending message on success
              pendingMessageRef.current = null;
              // If this was a new conversation, mark thread as persisted —
              // but only if user is still viewing the thread this stream created
              const userMessage = event.data?.userMessage;
              if (
                !threadId &&
                userMessage?.threadId &&
                onThreadChange &&
                streamCreatedThreadId
              ) {
                if (currentThreadIdRef.current === streamCreatedThreadId) {
                  onThreadChange(userMessage.threadId, true);
                }
              }
              trackChatbotMessageSuccess({
                thread_id: event.data?.userMessage?.threadId || threadId,
                reference_count:
                  event.data?.assistantMessage?.references?.length || 0,
              });
              setIsSending(false);
              break;
            }

            case 'error':
              // Clear pending message and track error for non-timeout errors
              // Timeouts are tracked separately via onTimeout → trackChatbotMessageTimeout
              if (event.data.error !== 'timeout') {
                pendingMessageRef.current = null;
                trackChatbotMessageError({
                  thread_id: threadId,
                  error: event.data.error,
                });
                console.error('Streaming error:', event.data.error);
              }
              setIsSending(false);
              break;

            case 'ping':
            case 'placeholder_update':
            case 'message_update':
            case 'message_reference':
              // All these events are handled in the query cache
              break;
          }
        },
        {
          onTimeout: (originalMessage: string) => {
            // Only restore message/show error if user hasn't switched threads
            if (currentThreadIdRef.current !== threadId) return;
            trackChatbotMessageTimeout({ thread_id: threadId });
            setMessageInput(originalMessage);
            setIsSending(false);
            pendingMessageRef.current = null;
            setShowTimeoutError(true);
          },
        }
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      pendingMessageRef.current = null;
      setIsSending(false);
    }
  };

  const handleSuggestionClick = (
    question: string,
    label: string,
    index: number
  ) => {
    trackChatbotSuggestionClick({
      thread_id: threadId,
      suggestion_label: label,
      suggestion_question: question,
      suggestion_index: index,
    });
    handleSendMessage(question);
  };

  return (
    <div className={`chatbot__container ${className}`}>
      <div className="chatbot__messages-container">
        <ChatMessages
          threadId={threadId}
          isThreadPersisted={isThreadPersisted}
          programId={programId}
          suggestions={suggestions}
          onSuggestionClick={handleSuggestionClick}
          isTyping={messageInput.length > 0}
        />
      </div>

      <div className="chatbot__input-wrapper">
        <ChatInput
          messageInput={messageInput}
          onMessageChange={setMessageInput}
          onSendMessage={handleSendMessage}
          isSending={isSending}
          shouldFocusInput={shouldFocusInput}
          onFocusHandled={onFocusHandled}
        />
      </div>
      {showTimeoutError && (
        <Toast
          title={t('chatbot.errors.timeout')}
          variant="error"
          defaultOpen
        />
      )}
    </div>
  );
};

export default Chatbot;
